import React, { useEffect, useRef, useState } from "react";
import {
  X,
  CaretRight,
  Camera,
  Sparkle,
  Keyboard,
  CircleNotch,
  Image as ImageIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../../../lib/api";
import { usePaywall } from "../../../lib/paywall";
import { resizeToDataUrl } from "../../../lib/imageUtils";
import { TextField, SelectField } from "./FormPrimitives";
import { ID_TYPES } from "./vaultSchema";

/* ---------- Method picker ---------- */
export function PhotoIdMethodPicker({ onPick, onCancel }) {
  const methods = [
    { id: "scan", Icon: Sparkle, title: "Scan ID with camera", sub: "Use AI to auto-fill the fields", color: "#E55B2C", soft: "#FDE6D8" },
    { id: "manual", Icon: Keyboard, title: "Enter manually", sub: "Type each field yourself", color: "#0E2C54", soft: "#EAF2FB" },
    { id: "photo", Icon: ImageIcon, title: "Save photo only", sub: "Store an image of your ID, no fields", color: "#2E7D5A", soft: "#DCEFE3" },
  ];
  return (
    <div
      className="mt-5 bg-white rounded-2xl shadow-soft border border-navy/10 p-4 space-y-3"
      data-testid="photo-id-method-picker"
    >
      <div className="flex items-center justify-between mb-1">
        <div className="font-jakarta font-extrabold text-navy text-base">
          How would you like to add this ID?
        </div>
        <button
          type="button"
          onClick={onCancel}
          aria-label="Cancel"
          className="w-9 h-9 rounded-full bg-bone/70 flex items-center justify-center"
        >
          <X size={18} weight="bold" color="#0E2C54" />
        </button>
      </div>
      {methods.map((m) => (
        <button
          key={m.id}
          type="button"
          data-testid={`add-method-${m.id}`}
          onClick={() => onPick(m.id)}
          className="w-full bg-bone/40 hover:bg-bone/70 border border-navy/5 rounded-2xl p-3 flex items-center gap-3 text-left"
        >
          <div
            className="shrink-0 w-11 h-11 rounded-2xl flex items-center justify-center"
            style={{ backgroundColor: m.soft }}
          >
            <m.Icon size={22} weight="duotone" color={m.color} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-jakarta font-extrabold text-navy text-base leading-tight">
              {m.title}
            </div>
            <div className="text-navy-soft text-sm font-jakarta font-medium mt-0.5">
              {m.sub}
            </div>
          </div>
          <CaretRight size={16} weight="bold" color="#4A5D78" />
        </button>
      ))}
    </div>
  );
}

/* ---------- The form ---------- */
export default function PhotoIdForm({ mode, familyMembers, onSave, onCancel }) {
  const { handle402 } = usePaywall();
  const [draft, setDraft] = useState({
    id_type: "passport",
    holder: "self",
    holder_name: "",
    id_number: "",
    nationality: "",
    issuing_authority: "",
    date_of_expiry: "",
    photo: null,
  });
  const [scanning, setScanning] = useState(false);
  const photoRef = useRef(null);
  const scanRef = useRef(null);

  // Auto-trigger camera/file picker for scan / photo modes on mount.
  const triggeredRef = useRef(false);
  useEffect(() => {
    if (triggeredRef.current) return;
    triggeredRef.current = true;
    if (mode === "scan") setTimeout(() => scanRef.current?.click(), 200);
    if (mode === "photo") setTimeout(() => photoRef.current?.click(), 200);
  }, [mode]);

  async function onScanFile(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      toast.error("Please upload an image");
      return;
    }
    const dataUrl = await resizeToDataUrl(file, 1600, 0.82);
    setDraft((d) => ({ ...d, photo: dataUrl }));
    setScanning(true);
    try {
      const b64 = dataUrl.split(",", 2)[1];
      const r = await api.post(`/vault/scan-id`, {
        image_base64: b64,
        id_type: draft.id_type || null,
      });
      const f = r.data?.fields || {};
      setDraft((d) => ({
        ...d,
        id_type: f.id_type || d.id_type,
        holder_name:
          f.holder_name ||
          `${f.first_name || ""} ${f.last_name || ""}`.trim() ||
          d.holder_name,
        id_number: f.id_number || d.id_number,
        nationality: f.nationality || d.nationality,
        issuing_authority: f.issuing_authority || d.issuing_authority,
        date_of_expiry: f.date_of_expiry || d.date_of_expiry,
      }));
      const filledCount = Object.values(f).filter(Boolean).length;
      if (filledCount === 0) {
        toast.message(
          "Couldn't read this ID — try a brighter, sharper photo or fill manually"
        );
      } else {
        toast.success(`Auto-filled ${filledCount} fields — review before saving`);
      }
    } catch (err) {
      if (!handle402(err)) {
        toast.error(err?.response?.data?.detail || "Scan failed");
      }
    } finally {
      setScanning(false);
    }
  }

  async function onPhotoFile(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      toast.error("Please upload an image");
      return;
    }
    const dataUrl = await resizeToDataUrl(file, 1600, 0.82);
    setDraft((d) => ({ ...d, photo: dataUrl }));
  }

  function submit(e) {
    e?.preventDefault?.();
    const hasField = ["holder_name", "id_number", "nationality"].some(
      (k) => draft[k]?.trim()
    );
    if (mode === "photo" && !draft.photo) {
      toast.error("Add a photo of your ID first");
      return;
    }
    if (mode !== "photo" && !hasField) {
      toast.error("Fill at least one field");
      return;
    }
    onSave(draft);
  }

  const holderOptions = [
    { id: "self", label: "Me" },
    ...familyMembers.map((f) => ({ id: f.id, label: f.name || "Family member" })),
  ];

  return (
    <form
      onSubmit={submit}
      data-testid="photo-id-form"
      className="mt-5 bg-white rounded-2xl shadow-soft border border-navy/10 p-4 space-y-3"
    >
      <div className="flex items-center justify-between mb-1">
        <div className="font-jakarta font-extrabold text-navy text-base">
          {mode === "scan" ? "Scan ID" : mode === "photo" ? "Photo only" : "New ID"}
        </div>
        <button
          type="button"
          onClick={onCancel}
          aria-label="Cancel"
          className="w-9 h-9 rounded-full bg-bone/70 flex items-center justify-center"
        >
          <X size={18} weight="bold" color="#0E2C54" />
        </button>
      </div>

      {/* Photo preview / picker */}
      <div className="space-y-2">
        <div className="text-navy-soft text-xs font-jakarta font-bold uppercase tracking-wider">
          ID photo
        </div>
        {draft.photo ? (
          <div className="relative rounded-xl overflow-hidden border border-navy/10">
            <img src={draft.photo} alt="ID" className="w-full h-44 object-cover" />
            {scanning && (
              <div className="absolute inset-0 bg-navy/60 flex items-center justify-center gap-2 text-white font-jakarta font-bold">
                <CircleNotch size={20} weight="bold" className="animate-spin" />
                Reading your ID…
              </div>
            )}
            <button
              type="button"
              onClick={() => setDraft((d) => ({ ...d, photo: null }))}
              className="absolute top-2 right-2 bg-white rounded-full w-8 h-8 flex items-center justify-center shadow-soft"
              aria-label="Remove photo"
            >
              <X size={14} weight="bold" color="#0E2C54" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() =>
              mode === "manual" ? photoRef.current?.click() : scanRef.current?.click()
            }
            data-testid="photo-id-add-photo"
            className="w-full h-32 rounded-xl border-2 border-dashed border-navy/20 bg-bone/40 flex flex-col items-center justify-center gap-1.5 text-navy-soft font-jakarta font-medium"
          >
            <Camera size={28} weight="duotone" color="#0E2C54" />
            <span className="text-sm">Tap to add ID photo</span>
          </button>
        )}
        <input
          ref={scanRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={onScanFile}
          data-testid="photo-id-scan-input"
        />
        <input
          ref={photoRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={onPhotoFile}
          data-testid="photo-id-photo-input"
        />
      </div>

      {mode !== "photo" && (
        <>
          <SelectField
            label="ID type"
            testId="vault-field-id_type"
            value={draft.id_type}
            options={ID_TYPES}
            onChange={(v) => setDraft((d) => ({ ...d, id_type: v }))}
          />
          <SelectField
            label="Holder"
            testId="vault-field-holder"
            value={draft.holder}
            options={holderOptions}
            onChange={(v) => setDraft((d) => ({ ...d, holder: v }))}
            hint={
              familyMembers.length === 0
                ? "Tip: add Family members to assign IDs to them."
                : null
            }
          />
          <TextField
            label="Holder name"
            placeholder="Jane Q. Traveler"
            testId="vault-field-holder_name"
            value={draft.holder_name}
            onChange={(v) => setDraft((d) => ({ ...d, holder_name: v }))}
          />
          <TextField
            label={
              draft.id_type === "passport" ? "Passport number" : "License/ID number"
            }
            placeholder="A12345678"
            testId="vault-field-id_number"
            value={draft.id_number}
            onChange={(v) => setDraft((d) => ({ ...d, id_number: v }))}
          />
          <TextField
            label={
              draft.id_type === "drivers_license"
                ? "Issuing state"
                : "Nationality / Issuing country"
            }
            placeholder={draft.id_type === "drivers_license" ? "California" : "United States"}
            testId="vault-field-nationality"
            value={draft.nationality}
            onChange={(v) => setDraft((d) => ({ ...d, nationality: v }))}
          />
          <TextField
            label="Expires"
            placeholder="MM/YYYY"
            testId="vault-field-expiry"
            value={draft.date_of_expiry}
            onChange={(v) => setDraft((d) => ({ ...d, date_of_expiry: v }))}
          />
        </>
      )}

      {mode === "photo" && (
        <TextField
          label="Label"
          placeholder="My passport"
          testId="vault-field-photo_label"
          value={draft.holder_name}
          onChange={(v) => setDraft((d) => ({ ...d, holder_name: v }))}
        />
      )}

      <button
        type="submit"
        data-testid="vault-save-btn"
        disabled={scanning}
        className="w-full bg-orange text-white font-jakarta font-extrabold text-base rounded-full py-3 shadow-soft disabled:opacity-60"
      >
        Save to Vault
      </button>
    </form>
  );
}
